import type { KeyboardEvent } from 'react'

const ArrowKeySteps: Record<string, number> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
}

type RadioGroupKeyboardOptions = {
  /** Also check the radio focus moves to, as a native radio group does */
  selectOnMove?: boolean
}

/**
 * Keyboard support for a `role="radiogroup"` of `role="radio"` buttons: one
 * Tab stop (the checked radio, or the first) and arrow keys, Home and End to
 * move between the radios. Put `getTabIndex` and `onKeyDown` on each radio.
 */
export default function useRadioGroupKeyboard(
  checkedIndex: number,
  { selectOnMove = false }: RadioGroupKeyboardOptions = {},
) {
  const tabbableIndex = Math.max(checkedIndex, 0)

  function getTabIndex(index: number) {
    return index === tabbableIndex ? 0 : -1
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    const group = event.currentTarget.closest('[role="radiogroup"]')
    const radios = Array.from(
      group?.querySelectorAll<HTMLElement>('[role="radio"]:not(:disabled)') ??
        [],
    )

    if (!radios.length) {
      return
    }

    const currentIndex = radios.indexOf(event.currentTarget)
    const lastIndex = radios.length - 1
    let nextIndex: number

    if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = lastIndex
    } else if (event.key in ArrowKeySteps) {
      nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + ArrowKeySteps[event.key] + radios.length) %
            radios.length
    } else {
      return
    }

    event.preventDefault()

    const nextRadio = radios[nextIndex]
    nextRadio.focus()

    if (selectOnMove && nextRadio.getAttribute('aria-checked') !== 'true') {
      nextRadio.click()
    }
  }

  return { getTabIndex, onKeyDown }
}
